import { createContext, useCallback, useEffect, useRef, useState } from 'react'
import {
  MAX_VOICE_SECONDS,
  selectVoiceRecorderFormat,
  type VoiceRecorderFormat,
} from './messaging-media'

type MessagingVoiceLeaveGuard = {
  register: (
    conversationId: string,
    hasUnsavedVoice: boolean,
    discard: () => void,
  ) => () => void
}

export const MessagingVoiceLeaveGuardContext =
  createContext<MessagingVoiceLeaveGuard | null>(null)

export type VoiceRecorderErrorReason =
  | 'permission_denied'
  | 'unsupported'
  | 'interrupted'

type VoiceRecorderState =
  | { status: 'idle' }
  | { status: 'requesting' }
  | { status: 'recording'; elapsedSeconds: number; level: number }
  | {
      status: 'preview'
      file: File
      previewUrl: string
      elapsedSeconds: number
    }
  | { status: 'error'; reason: VoiceRecorderErrorReason }

type VoiceRecorderController = {
  state: VoiceRecorderState
  start: () => Promise<void>
  stop: () => void
  cancel: () => void
}

const IDLE_STATE: VoiceRecorderState = { status: 'idle' }

function permissionWasDenied(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === 'NotAllowedError' || error.name === 'SecurityError')
  )
}

export function useMessagingVoiceRecorder(
  conversationKey: string,
  onPreviewReady?: (file: File) => void,
): VoiceRecorderController {
  const [snapshot, setSnapshot] = useState<{
    key: string
    state: VoiceRecorderState
  }>(() => ({ key: conversationKey, state: IDLE_STATE }))
  // Persist the transition so A -> B -> A cannot revive A's revoked Blob URL.
  if (snapshot.key !== conversationKey) {
    setSnapshot({ key: conversationKey, state: IDLE_STATE })
  }
  const state = snapshot.key === conversationKey ? snapshot.state : IDLE_STATE
  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const elapsedRef = useRef(0)
  const startedAtRef = useRef(0)
  const previewUrlRef = useRef<string | null>(null)
  const sessionRef = useRef(0)
  const stoppingRef = useRef(false)
  const onPreviewReadyRef = useRef(onPreviewReady)

  useEffect(() => {
    onPreviewReadyRef.current = onPreviewReady
  }, [onPreviewReady])

  const setRecorderState = useCallback(
    (
      update:
        | VoiceRecorderState
        | ((current: VoiceRecorderState) => VoiceRecorderState),
    ) => {
      setSnapshot((current) => {
        const currentState =
          current.key === conversationKey ? current.state : IDLE_STATE
        return {
          key: conversationKey,
          state:
            typeof update === 'function' ? update(currentState) : update,
        }
      })
    },
    [conversationKey],
  )

  const revokePreview = useCallback(() => {
    if (!previewUrlRef.current) return
    URL.revokeObjectURL(previewUrlRef.current)
    previewUrlRef.current = null
  }, [])

  const releaseCapture = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    sourceNodeRef.current?.disconnect()
    sourceNodeRef.current = null
    if (audioContextRef.current) {
      void audioContextRef.current.close().catch(() => undefined)
      audioContextRef.current = null
    }
    for (const track of streamRef.current?.getTracks() ?? []) track.stop()
    streamRef.current = null
    recorderRef.current = null
    stoppingRef.current = false
  }, [])

  const interrupt = useCallback(
    (session: number) => {
      if (sessionRef.current !== session) return
      sessionRef.current += 1
      releaseCapture()
      chunksRef.current = []
      setRecorderState({ status: 'error', reason: 'interrupted' })
    },
    [releaseCapture, setRecorderState],
  )

  const finishRecording = useCallback(
    (session: number, format: VoiceRecorderFormat) => {
      if (sessionRef.current !== session) return
      const elapsedSeconds = Math.max(
        1,
        Math.min(
          MAX_VOICE_SECONDS,
          Math.ceil((performance.now() - startedAtRef.current) / 1_000),
        ),
      )
      const chunks = chunksRef.current
      chunksRef.current = []
      releaseCapture()
      revokePreview()
      const file = new File(chunks, `voice-message.${format.extension}`, {
        type: format.uploadType,
      })
      if (file.size === 0) {
        setRecorderState({ status: 'error', reason: 'interrupted' })
        return
      }
      const previewUrl = URL.createObjectURL(file)
      previewUrlRef.current = previewUrl
      setRecorderState({ status: 'preview', file, previewUrl, elapsedSeconds })
      onPreviewReadyRef.current?.(file)
    },
    [releaseCapture, revokePreview, setRecorderState],
  )

  const stopSession = useCallback(
    (session: number) => {
      const recorder = recorderRef.current
      if (
        sessionRef.current !== session ||
        !recorder ||
        recorder.state === 'inactive' ||
        stoppingRef.current
      ) {
        return
      }
      stoppingRef.current = true
      try {
        recorder.stop()
      } catch {
        interrupt(session)
      }
    },
    [interrupt],
  )

  const cancel = useCallback(() => {
    sessionRef.current += 1
    const recorder = recorderRef.current
    releaseCapture()
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop()
      } catch {
        // Capture resources are already released and the session is invalidated.
      }
    }
    chunksRef.current = []
    elapsedRef.current = 0
    revokePreview()
    setRecorderState(IDLE_STATE)
  }, [releaseCapture, revokePreview, setRecorderState])

  const start = useCallback(async () => {
    cancel()
    const Recorder = globalThis.MediaRecorder
    if (!Recorder || typeof Recorder.isTypeSupported !== 'function') {
      setRecorderState({ status: 'error', reason: 'unsupported' })
      return
    }
    const format = selectVoiceRecorderFormat(Recorder.isTypeSupported.bind(Recorder))
    if (!format || !navigator.mediaDevices?.getUserMedia) {
      setRecorderState({ status: 'error', reason: 'unsupported' })
      return
    }

    const session = sessionRef.current
    setRecorderState({ status: 'requesting' })
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (error) {
      if (sessionRef.current !== session) return
      setRecorderState({
        status: 'error',
        reason: permissionWasDenied(error) ? 'permission_denied' : 'interrupted',
      })
      return
    }
    if (sessionRef.current !== session) {
      for (const track of stream.getTracks()) track.stop()
      return
    }

    try {
      const recorder = new Recorder(stream, { mimeType: format.mimeType })
      streamRef.current = stream
      recorderRef.current = recorder
      chunksRef.current = []
      elapsedRef.current = 0
      startedAtRef.current = performance.now()
      stoppingRef.current = false
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data)
      }
      recorder.onstop = () => finishRecording(session, format)
      recorder.onerror = () => interrupt(session)

      if (typeof globalThis.AudioContext === 'function') {
        const audioContext = new AudioContext()
        const analyser = audioContext.createAnalyser()
        analyser.fftSize = 256
        const sourceNode = audioContext.createMediaStreamSource(stream)
        sourceNode.connect(analyser)
        audioContextRef.current = audioContext
        sourceNodeRef.current = sourceNode
        const values = new Uint8Array(analyser.frequencyBinCount)
        const sampleLevel = () => {
          if (sessionRef.current !== session || !recorderRef.current) return
          analyser.getByteTimeDomainData(values)
          let distance = 0
          for (const value of values) distance += Math.abs(value - 128)
          const level = Math.min(1, distance / (values.length * 64))
          setRecorderState((current) =>
            current.status === 'recording' ? { ...current, level } : current,
          )
          animationFrameRef.current = requestAnimationFrame(sampleLevel)
        }
        animationFrameRef.current = requestAnimationFrame(sampleLevel)
      }

      recorder.start(250)
      setRecorderState({ status: 'recording', elapsedSeconds: 0, level: 0 })
      timerRef.current = setInterval(() => {
        if (sessionRef.current !== session) return
        elapsedRef.current = Math.min(MAX_VOICE_SECONDS, Math.floor((performance.now() - startedAtRef.current) / 1_000))
        setRecorderState((current) =>
          current.status === 'recording'
            ? { ...current, elapsedSeconds: elapsedRef.current }
            : current,
        )
        if (elapsedRef.current === MAX_VOICE_SECONDS) stopSession(session)
      }, 1_000)
    } catch {
      for (const track of stream.getTracks()) track.stop()
      interrupt(session)
    }
  }, [cancel, finishRecording, interrupt, setRecorderState, stopSession])

  const stop = useCallback(() => {
    stopSession(sessionRef.current)
  }, [stopSession])

  useEffect(() => {
    return () => {
      sessionRef.current += 1
      const recorder = recorderRef.current
      releaseCapture()
      if (recorder && recorder.state !== 'inactive') {
        try {
          recorder.stop()
        } catch {
          // The browser already ended the recorder.
        }
      }
      revokePreview()
    }
  }, [conversationKey, releaseCapture, revokePreview])

  return { state, start, stop, cancel }
}
